import type { UserDataLoginRecord } from '@navio/nsdk';
import { URLKeys, noddiAsync } from 'noddi-async';
import type { ErrorCodes } from 'noddi-ui-web';
import { ApiErrorMessage, NoddiButton, NoddiInfoBanner, useAppForm } from 'noddi-ui-web';
import { commonTexts } from 'noddi-util';
import { useState } from 'react';
import { z } from 'zod';
import { type UserSessionData } from '../../contexts/AuthContext';
import { useAuthContext } from '../../contexts/useAuthContext';
import { type UserRoleType } from '../../types/roles';
import { finalizeLoginSession } from './loginSession';
import { MfaChallengeStep } from './MfaChallengeStep';
import { PasswordResetFlow, type PasswordResetFlowTranslations } from './PasswordResetFlow';
import { createEmailLoginSchema } from './passwordResetSchemas';

interface EmailLoginTranslations {
  emailAddress: string;
  password: string;
  logIn: string;
  accessDeniedLoginMessage: string;
  forgotPassword?: string;
  passwordResetSuccess?: (email: string) => string;
  invalidEmail?: string;
  passwordRequired?: string;
  passwordReset?: PasswordResetFlowTranslations;
}

interface EmailLoginProps {
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  requiredLoginLevels?: UserRoleType[];
  onLoginSuccess?: (userData: UserSessionData) => void;
  translations?: EmailLoginTranslations;
  trackUserIdentity?: (userData: UserDataLoginRecord) => void;
  enablePasswordReset?: boolean;
  enableSmsPasswordReset?: boolean;
  initialEmail?: string;
  errorCodes?: ErrorCodes;
}

const defaultTexts = {
  emailAddress: commonTexts.email,
  password: commonTexts.password,
  logIn: commonTexts.logIn,
  accessDeniedLoginMessage: commonTexts.accessDeniedLoginMessage,
  forgotPassword: 'Forgot password?',
  passwordResetSuccess: (email: string) => `We've sent a link to reset your password to ${email}.`,
  invalidEmail: 'Invalid email format',
  passwordRequired: 'Password is required'
};

type FormValues = z.input<ReturnType<typeof createEmailLoginSchema>>;

export const EmailLogin = ({
  setErrorMessage,
  trackUserIdentity,
  requiredLoginLevels,
  onLoginSuccess,
  translations,
  enablePasswordReset = false,
  enableSmsPasswordReset,
  initialEmail,
  errorCodes
}: EmailLoginProps) => {
  const { loginUser } = useAuthContext();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmailSentTo, setResetEmailSentTo] = useState<string>();
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(null);

  const texts = { ...defaultTexts, ...translations };

  const finalizeLogin = (session: UserSessionData) => {
    finalizeLoginSession({
      session,
      loginUser,
      requiredLoginLevels,
      accessDeniedMessage: texts.accessDeniedLoginMessage,
      onAccessDenied: setErrorMessage,
      trackUserIdentity,
      onLoginSuccess
    });
  };

  const { mutateAsync: login, error } = noddiAsync.usePost({
    type: URLKeys.postEmailLogin,
    queryConfig: {
      onSuccess: (res) => {
        if (res.data.mfaRequired && res.data.mfaChallengeToken) {
          setMfaChallengeToken(res.data.mfaChallengeToken);
          return;
        }
        const { token, user } = res.data;
        if (!token || !user) {
          setErrorMessage(texts.accessDeniedLoginMessage);
          return;
        }
        finalizeLogin({ token, user });
      }
    }
  });

  const form = useAppForm({
    defaultValues: { email: initialEmail ?? '', password: '' } satisfies FormValues,
    validators: {
      onSubmit: createEmailLoginSchema(texts)
    },
    onSubmit: async ({ value }) => {
      await login({ email: value.email, password: value.password });
    }
  });

  if (mfaChallengeToken) {
    return (
      <MfaChallengeStep
        challengeToken={mfaChallengeToken}
        onVerified={(data) => finalizeLogin({ token: data.token, user: data.user })}
        setErrorMessage={setErrorMessage}
        onBack={() => setMfaChallengeToken(null)}
      />
    );
  }

  if (showPasswordReset) {
    return (
      <PasswordResetFlow
        initialEmail={form.getFieldValue('email')}
        enableSmsReset={enableSmsPasswordReset}
        onSuccess={(sentToEmail) => {
          setShowPasswordReset(false);
          setResetEmailSentTo(sentToEmail);
        }}
        onCancel={() => setShowPasswordReset(false)}
        translations={texts.passwordReset}
        errorCodes={errorCodes}
      />
    );
  }

  return (
    <>
      {resetEmailSentTo && (
        <NoddiInfoBanner
          variant='success'
          texts={[{ value: texts.passwordResetSuccess(resetEmailSentTo) }]}
          onClose={() => setResetEmailSentTo(undefined)}
        />
      )}
      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-4'>
            <form.AppField name='email'>
              {/* oxlint-disable-next-line jsx-a11y/no-autofocus -- first field of the login form, which is the entire page */}
              {(field) => <field.Input autoFocus type='email' label={texts.emailAddress} autoComplete='username' />}
            </form.AppField>
            <form.AppField name='password'>
              {(field) => <field.Input type='password' label={texts.password} autoComplete='current-password' />}
            </form.AppField>
            {enablePasswordReset && (
              <div className='flex justify-end'>
                <NoddiButton type='button' variant='link' size='small' onClick={() => setShowPasswordReset(true)}>
                  {texts.forgotPassword}
                </NoddiButton>
              </div>
            )}
            {error && errorCodes && <ApiErrorMessage errorCodes={errorCodes} error={error} />}
            <form.SubscribeButton label={texts.logIn} className='w-full' keepsFocus />
          </div>
        </form>
      </form.AppForm>
    </>
  );
};
